/**
 * Regulatory Monitor Page — Regulatory Updates Feed
 *
 * Features:
 *   - Regulatory updates feed with impact level badges
 *   - Mark as read
 *   - Filter by regulation (SFDR, Taxonomy, CSRD)
 *   - Create update form (admin only)
 *
 * Data from:
 *   GET  /api/v1/impact-core/regulatory/updates/  — updates list
 *   PATCH /api/v1/impact-core/regulatory/updates/:id/read — mark as read
 *   POST /api/v1/impact-core/regulatory/updates/  — create update (admin)
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  Plus,
  X,
  Check,
  Filter,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Info,
  Circle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { apiClient } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useTranslation } from '@/i18n';

// ─── Types ──────────────────────────────────────────────────────

interface RegulatoryUpdate {
  id: number;
  title: string;
  summary: string;
  regulation: string;
  impact_level: 'critical' | 'high' | 'medium' | 'low';
  effective_date?: string;
  source_url?: string;
  is_read: boolean;
  created_by?: string;
  created_at: string;
}

interface UpdateCreatePayload {
  title: string;
  summary: string;
  regulation: string;
  impact_level: string;
  effective_date?: string;
  source_url?: string;
}

const IMPACT_STYLES: Record<string, { variant: 'danger' | 'warning' | 'info' | 'default'; icon: React.ReactNode; label: string }> = {
  critical: { variant: 'danger', icon: <AlertCircle className="h-3.5 w-3.5" />, label: 'Critical' },
  high: { variant: 'warning', icon: <AlertTriangle className="h-3.5 w-3.5" />, label: 'High' },
  medium: { variant: 'info', icon: <Info className="h-3.5 w-3.5" />, label: 'Medium' },
  low: { variant: 'default', icon: <Circle className="h-3.5 w-3.5" />, label: 'Low' },
};

const REGULATIONS = ['All', 'SFDR', 'Taxonomy', 'CSRD', 'MiFID', 'Other'] as const;

// ─── Main Component ─────────────────────────────────────────────

export function RegulatoryPage() {
  const { icRole } = useAuth();
  const queryClient = useQueryClient();
  const [filterRegulation, setFilterRegulation] = useState<string>('All');
  const [showCreate, setShowCreate] = useState(false);
  const isAdmin = icRole === 'org_admin';

  const updatesQuery = useQuery<RegulatoryUpdate[]>({
    queryKey: ['ic', 'regulatory', 'updates'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/regulatory/updates/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiClient.patch(`/api/v1/impact-core/regulatory/updates/${id}/read`);
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ic', 'regulatory', 'updates'] });
    },
  });

  const updates = updatesQuery.data ?? [];

  const filtered = useMemo(() => {
    if (filterRegulation === 'All') return updates;
    return updates.filter((u) => u.regulation === filterRegulation);
  }, [updates, filterRegulation]);

  const unreadCount = updates.filter((u) => !u.is_read).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">Regulatory Monitor</h1>
          <p className="text-xs text-ic-text-tertiary">
            {unreadCount > 0 ? `${unreadCount} unread update${unreadCount !== 1 ? 's' : ''}` : 'All updates read'}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Update
          </button>
        )}
      </div>

      {/* Summary Cards */}
      {!updatesQuery.isLoading && updates.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {(['critical', 'high', 'medium', 'low'] as const).map((level) => {
            const count = updates.filter((u) => u.impact_level === level).length;
            const style = IMPACT_STYLES[level];
            return (
              <div key={level} className="rounded-lg border border-ic-border bg-ic-card p-3">
                <div className="flex items-center gap-2 text-ic-text-tertiary">
                  {style?.icon}
                  <span className="text-[10px] uppercase tracking-wider">{style?.label}</span>
                </div>
                <p className="mt-1 text-xl font-semibold text-ic-text-primary tracking-tight">{count}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-ic-text-tertiary" />
        <div className="flex gap-1">
          {REGULATIONS.map((reg) => (
            <button
              key={reg}
              onClick={() => setFilterRegulation(reg)}
              className={`rounded-full px-3 py-1 text-[10px] font-medium transition-colors ${
                filterRegulation === reg
                  ? 'bg-ic-accent/15 text-ic-accent border border-ic-accent/25'
                  : 'bg-ic-card text-ic-text-tertiary border border-ic-border hover:text-ic-text-secondary'
              }`}
            >
              {reg}
            </button>
          ))}
        </div>
      </div>

      {/* Updates Feed */}
      {updatesQuery.isLoading ? (
        <LoadingState />
      ) : updatesQuery.isError ? (
        <ErrorMsg msg="Failed to load regulatory updates." />
      ) : filtered.length === 0 ? (
        <EmptyMsg msg={filterRegulation === 'All' ? 'No regulatory updates yet.' : `No ${filterRegulation} updates found.`} />
      ) : (
        <div className="space-y-3">
          {filtered.map((update) => {
            const impact = IMPACT_STYLES[update.impact_level];
            return (
              <div
                key={update.id}
                className={`rounded-lg border bg-ic-card p-4 transition-colors ${
                  update.is_read ? 'border-ic-border' : 'border-ic-accent/30 bg-ic-accent/[0.02]'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      {!update.is_read && (
                        <span className="h-2 w-2 rounded-full bg-ic-accent flex-shrink-0" />
                      )}
                      <h3 className={`text-xs font-semibold ${
                        update.is_read ? 'text-ic-text-secondary' : 'text-ic-text-primary'
                      }`}>
                        {update.title}
                      </h3>
                    </div>

                    <p className="text-[10px] text-ic-text-tertiary mb-2 line-clamp-2">
                      {update.summary}
                    </p>

                    <div className="flex items-center gap-3 flex-wrap">
                      <StatusBadge
                        label={impact?.label ?? update.impact_level}
                        variant={impact?.variant ?? 'default'}
                        size="sm"
                      />
                      <StatusBadge label={update.regulation} variant="info" size="sm" />
                      <span className="text-[10px] text-ic-text-tertiary">
                        {format(parseISO(update.created_at), 'dd MMM yyyy')}
                      </span>
                      {update.effective_date && (
                        <span className="text-[10px] text-ic-text-tertiary">
                          Effective: {format(parseISO(update.effective_date), 'dd MMM yyyy')}
                        </span>
                      )}
                      {update.source_url && (
                        <a
                          href={update.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-ic-accent hover:underline"
                        >
                          Source
                        </a>
                      )}
                    </div>
                  </div>

                  {!update.is_read && (
                    <button
                      onClick={() => markReadMutation.mutate(update.id)}
                      disabled={markReadMutation.isPending}
                      className="flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium text-ic-accent hover:bg-ic-accent/10 disabled:opacity-50 transition-colors flex-shrink-0"
                    >
                      <Check className="h-3 w-3" />
                      Mark read
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      {showCreate && (
        <UpdateCreateDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['ic', 'regulatory', 'updates'] });
          }}
        />
      )}
    </div>
  );
}

// ─── Create Update Dialog ───────────────────────────────────────

function UpdateCreateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [regulation, setRegulation] = useState('SFDR');
  const [impactLevel, setImpactLevel] = useState('medium');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: async (payload: UpdateCreatePayload) => {
      const res = await apiClient.post('/api/v1/impact-core/regulatory/updates/', payload);
      return res.data;
    },
    onSuccess: () => onCreated(),
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setErrors({ _general: axiosErr.response?.data?.detail ?? 'Failed to create update.' });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    if (!title.trim()) { setErrors({ title: 'Title is required' }); return; }
    if (!summary.trim()) { setErrors({ summary: 'Summary is required' }); return; }

    const payload: UpdateCreatePayload = {
      title: title.trim(),
      summary: summary.trim(),
      regulation,
      impact_level: impactLevel,
      effective_date: effectiveDate || undefined,
      source_url: sourceUrl.trim() || undefined,
    };

    mutation.mutate(payload);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-ic-accent" />
            <h2 className="text-sm font-semibold text-ic-text-primary">Add Regulatory Update</h2>
          </div>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
              {errors._general}
            </div>
          )}

          <FormField label="Title *" error={errors.title}>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. SFDR Level 2 RTS update Q2 2026"
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
            />
          </FormField>

          <FormField label="Summary *" error={errors.summary}>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              placeholder="Brief description of the regulatory change and its implications..."
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none resize-none"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Regulation">
              <select
                value={regulation}
                onChange={(e) => setRegulation(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="SFDR">{t('labels.sfdr')}</option>
                <option value="Taxonomy">{t('labels.euTaxonomy')}</option>
                <option value="CSRD">{t('labels.csrd')}</option>
                <option value="MiFID">{t('labels.mifid')}</option>
                <option value="Other">{t('labels.other')}</option>
              </select>
            </FormField>
            <FormField label="Impact Level">
              <select
                value={impactLevel}
                onChange={(e) => setImpactLevel(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="critical">{t('labels.critical')}</option>
                <option value="high">{t('labels.high')}</option>
                <option value="medium">{t('labels.medium')}</option>
                <option value="low">{t('labels.low')}</option>
              </select>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Effective Date">
              <input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              />
            </FormField>
            <FormField label="Source URL">
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://..."
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
              />
            </FormField>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Publishing...' : 'Publish Update'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">{label}</label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex h-48 items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-ic-text-tertiary" />
    </div>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  return (
    <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
      <p className="text-xs text-red-400">{msg}</p>
    </div>
  );
}

function EmptyMsg({ msg }: { msg: string }) {
  return (
    <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
      <p className="text-xs text-ic-text-tertiary">{msg}</p>
    </div>
  );
}
